import { Component, OnDestroy, OnInit, ChangeDetectorRef, NgZone } from '@angular/core';
import { FormGroup, FormBuilder, Validators, FormControl } from '@angular/forms';
import { Router } from '@angular/router';
import { Subscription } from 'rxjs';
import { ToastrService } from 'ngx-toastr';

import { AuthenticationService } from '../services/loginServices/authentication.service';
import { DashboardService } from '../services/dashboardServices/dashboard.service';
import { SpinnerService } from '../spinner/spinner.service';
import { EncryptionService } from '../services/loginServices/encryption.service';
import UserExperior from "user-experior-web";
import { environment } from 'src/environments/environment';
import { ConfigService } from '../services/loginServices/configservice.service';

@Component({
  selector: 'app-login-page',
  templateUrl: './login-page.component.html',
  styleUrls: ['./login-page.component.less']
})
export class LoginPageComponent implements OnInit, OnDestroy {

  selected = 'branch_1';
  loginForm!: FormGroup;
  showSpinner!: boolean;
  showSpinnerSubs!: Subscription;
  users: Array<any> = [];
  userIsAuthenticated: boolean = false;
  passwordHide: boolean = true;
  selectUserBranch: boolean = false;
  branchSelect = new FormControl('', [Validators.required]);
  searchBranchControl = new FormControl('');
  branch: Array<any> = []; // Complete list of branches
  filteredBranches: Array<any> = []; // Filtered branches for display
  isSearching: boolean = false; // Tracks if search input has text
  isDropdownOpen = false; // Tracks if dropdown is open
  private authListenerSubs!: Subscription;

  constructor(private fb: FormBuilder,
    private _authService: AuthenticationService,
    private _dashboardService: DashboardService,
    private router: Router,
    private toastr: ToastrService,
    private _spinnerService: SpinnerService,
    private encryptionService: EncryptionService, // Inject EncryptionService
    private configService: ConfigService, // Inject ConfigService
    private cdr: ChangeDetectorRef, // Inject ChangeDetectorRef
    private ngZone: NgZone // Inject NgZone
   ) { }

  ngOnInit(): void {

    this.authListenerSubs = this._authService.getAuthStatusListener().subscribe(isAuthenticated => {
      this.userIsAuthenticated = isAuthenticated;
    });

    this.showSpinnerSubs = this._spinnerService.getVisibility().subscribe(res => {
      this.showSpinner = res;
    });

    this.loginForm = this.fb.group({
      username: ['', Validators.required],
      password: ['', Validators.required]
    });

    // Filter branches as user types in the search input
    this.searchBranchControl.valueChanges.subscribe(value => {
      this.isSearching = !!value; // Set isSearching to true if there is input, false otherwise
      this.filteredBranches = this.branch.filter(option =>
        option.branch_name.toLowerCase().includes(value.toLowerCase())
      );
    });
  }

  onDropdownOpened(opened: boolean) {
    this.isDropdownOpen = opened;
  }

  ngOnDestroy() {
    this.authListenerSubs.unsubscribe();
    this.showSpinnerSubs.unsubscribe();
  };

  private getKrakendUrl(): string {
    const krakendUrl = this.configService.getKrakendUrl();
    if (!krakendUrl) {
      throw new Error("Krakend URL is not available.");
    }
    return krakendUrl;
  }

  async login() {
    const url = this.getKrakendUrl();
    if (this.loginForm.invalid) {
      return;
    } else {
      const data = { 
        id: null,
        username: this.username?.value,
        password: this.password?.value
      };
      if (url) {
        try {
          const key = await this.encryptionService.base64ToCryptoKey();
          await this.encryptionService.printKeyInHex(key);

          const encryptedData = await this.encryptionService.encrypt(JSON.stringify(data), key);

          const payload = {
            encrypted_data: encryptedData
          };

          localStorage.setItem('username', data.username);

          this._authService.postUser(payload).subscribe((response: any) => {
            if (response.token) {
              this._auth_service_or_setLogin(response);
            } else {
              this.toastr.error('Login unsuccessful!');
            }
          }, (err: any) => {
            this.handleError(err);
          });
        } catch (error) {
          console.error('Error:', error);
          this.toastr.error('Encryption or key conversion failed.');
        }
      } else {
        this._authService.postUser(data).subscribe((response: any) => {
          if (response.token) {
            this._auth_service_or_setLogin(response);
          } else {
            this.toastr.error('Login unsuccessful');
          }
        }, (err: any) => {
          this.handleError(err);
        });
      }
    }

    const ue = new UserExperior();
    ue.startRecording('DvRvStPZG9uOmNvcmU6ZHZydi1pbi0xOmRldm8vMnNxVkhNbUNtbTpwbHVnX3NldHRpbmcvMV9ffHxfXzIwMjQtMDktMDQgMDY6Mzg6MTUuODQ1MTA2MjY3ICswMDAwIFVUQw==xlxendsDvRv', {
      sessionReplay: {
        maskAllInputs: true,
        maskInputOptions: {
          password: true,
          email: true,
          tel: true,
          color: false,
          date: false,
          'datetime-local': false,
          month: false,
          number: false,
          range: false,
          search: false,
          text: true,
          time: false,
          url: false,
          week: false,
          textarea: false,
          select: false,
        }
      }
    });
    // Fetch the user ID from localStorage
    let userId: string | null = localStorage.getItem("username");

    // Check if the userId is a valid string
    if (!userId || typeof userId !== 'string' || userId.trim() === "") {
      console.error('User ID is missing or invalid.');
      userId = 'guest';  // Assign a default ID if necessary
    }
    ue.setUserIdentifier(environment.env + userId);
    // ue.setUserIdentifier(localStorage.getItem("in_userid"));
    ue.logEvent("Attempting to log in", {
      "organisation": "Navadhan internal app",
      "user_type": "dev",
      "api_endpoint": "Your API endpoint URL here",  // Specify the actual login API endpoint
      "http_method": "POST",                        // Specify the HTTP method
      "api_token": "Your API token here"            // Add this if the token is available at this stage
    });

  }

  // Helper extracted to reuse logic for both encrypted and plain login flows
  private _auth_service_or_setLogin(response: any) {
    this._authService.setLoginData(response);

    // Determine if this is first login (response may provide flag or fallback to localStorage)
    const respFlag = response?.is_first_login ?? localStorage.getItem('is_first_login');
    const isFirstLogin = String(respFlag) === '1' || String(respFlag) === 'true';

    // Fetch branches
    this._dashboardService.getUserBranch().subscribe((resp: any) => {
      this.branch = resp.branch;
      this.filteredBranches = this.branch; // Initially show all branches
      
      if (isFirstLogin) {
        // First login users skip branch selection - set default branch and proceed
        this.selectUserBranch = false;
        if (this.branch && this.branch.length > 0) {
          // Set the first branch as default for first-time login users
          this._authService.setUserBranchData(this.branch[0].branch_id);
        }
      } else {
        // Show branch selection for non-first-login users
        // Use NgZone.run to ensure proper change detection
        this.ngZone.run(() => {
          this.selectUserBranch = true;
        });
      }
    }, (err) => {
      this.handleError(err);
    });
  }

  private handleError(err: any) {
    const errorMessage = err?.error?.message || 'An unexpected error occurred';
    this.toastr.error(errorMessage, 'Error!!');
  }

  proceedWithBranch() {
    if (this.branchSelect.invalid || this.branchSelect.value == "") {
      this.branchSelect.markAsTouched();
    } else {
      this._authService.setUserBranchData(this.branchSelect.value);
    }
  }

  getusernameErrorMsg() {
    return this.username!.hasError('required') ? 'Please enter your username.' : '';
  }

  getPasswordErrorMsg() {
    return this.password!.hasError('required') ? 'Please enter your password.' : '';
  }

  get username() {
    return this.loginForm.get('username');
  }

  get password() {
    return this.loginForm.get('password');
  }

}